import { useMemo, useState, type ReactNode } from "react";
import { MemoryRouter } from "react-router-dom";
import { ConnectProto } from "@racoon/api";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { PickupLocationModal } from "~/features/pickup-location-modal/ui/pickup-location.modal";
import { useLazyGetRouteQuery } from "~/shared/api/routes/route.grpc";
import { useRouteJobsModalOutsideRouter } from "~/pages/dashboard/routes/hooks/useRouteJobsModal";
import { formatPickupLocationToForm } from "../../pickup-locations/model/formatPickupLocationToForm";

// Figma "Link button": Body Regular in Text/body. The modal's title wrapper sets a bold
// text-lg, so the weight and size are spelled out rather than inherited.
const linkClass =
  "flex h-6 min-w-0 max-w-[200px] items-center gap-1 rounded-lg p-1 text-right text-sm font-normal leading-4 tracking-[-0.01em] text-text-body hover:bg-slate-100";

// Without onClick the name is plain text
const LinkButton = ({ onClick, children }: { onClick?: () => void; children: ReactNode }) =>
  onClick ? (
    <button type="button" className={linkClass} onClick={onClick}>
      <span className="truncate">{children}</span>
    </button>
  ) : (
    <span className={`${linkClass} hover:bg-transparent`}>
      <span className="truncate">{children}</span>
    </span>
  );

/**
 * The order form's header links — the site, the customer, the order and the trip, each
 * opening its own card. Shared by the job card and the create dialog, which fills them in
 * as the dispatcher picks.
 */
export const JobHeadingLinks = ({
  location,
  customer,
  orderNumber,
  routeId,
  readOnly,
}: {
  location?: ConnectProto.PickupLocation;
  customer?: ConnectProto.Organization;
  orderNumber?: string;
  routeId?: bigint;
  /** Another organization's job, or not yet known as ours: its customer and trip cards stay shut. */
  readOnly?: boolean;
}) => {
  const { activeCompanyId } = useAuthSession();
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: activeCompanyId ?? undefined,
  });
  const [fetchRoute] = useLazyGetRouteQuery();
  const { openRouteJobs } = useRouteJobsModalOutsideRouter();
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const isOwnForWrite = useIsOwnOrganizationForWrite();
  const locationForm = useMemo(
    () => location && { ...formatPickupLocationToForm(location), id: Number(location.id) },
    [location],
  );

  // No unwrap: a failed fetch resolves without data, and the global error toaster reports it
  const openRoute = (id: bigint) =>
    fetchRoute({ id, ownerOrganizationId: BigInt(activeCompanyId ?? 0) }).then(
      ({ data }) => data && openRouteJobs(data),
    );

  return (
    <div className="flex min-w-0 items-center gap-2.5" data-testid="job-heading-links">
      {location && <LinkButton onClick={() => setIsLocationOpen(true)}>{location.name}</LinkButton>}
      {customer && (
        <LinkButton onClick={readOnly ? undefined : () => openCustomerCard(customer)}>
          {customer.name}
        </LinkButton>
      )}
      {/* No order card to open yet */}
      {orderNumber && (
        <span className={`${linkClass} flex-shrink-0 hover:bg-transparent`}>{orderNumber}</span>
      )}
      {routeId ? (
        // Fetched as the active company's: another provider's trip would not load
        <LinkButton onClick={readOnly ? undefined : () => openRoute(routeId)}>
          M{String(routeId)}
        </LinkButton>
      ) : null}
      {locationForm && (
        <MemoryRouter>
          <PickupLocationModal
            location={locationForm}
            open={isLocationOpen}
            onOpenChange={setIsLocationOpen}
            hideTrigger
            readOnly={
              isOwnForWrite(location?.ownerOrganizationId, location?.serviceProviderId) !== true
            }
          />
        </MemoryRouter>
      )}
    </div>
  );
};
